import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { trainingsService, Training } from '../../services/trainings.service';
import sessionsService from '../../services/sessions.service';
import CountdownTimer from '../../components/common/CountdownTimer';
import { formatTrainingLocation, getCityDisplay } from '../../utils/address';
import { API_ENDPOINTS } from '../../config/api';
import {
  ArrowLeft,
  Star,
  Clock,
  MapPin,
  Users,
  Calendar,
  Award,
  BookOpen,
  Download,
  Share2,
  CheckCircle,
  Building2,
  Globe,
  Target,
  TrendingUp,
  Shield,
  Zap,
  FileText,
  Play,
  Phone,
  Mail,
  ExternalLink,
  ThumbsUp,
  MessageCircle,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  MoreHorizontal,
  Trash2,
  Loader2
} from 'lucide-react';

// Animation variants for modern landing page effects
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 60,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const slideInVariants = {
  hidden: {
    opacity: 0,
    x: -50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1
  }
};

const scaleVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0
  }
};

const pulseVariants = {
  hover: {
    scale: 1.05
  },
  tap: {
    scale: 0.95
  }
};

// Scroll reveal component
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: {
          opacity: 1,
          y: 0
        }
      }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const ViewTrainingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [training, setTraining] = useState<Training | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    description: false,
    objectives: false,
    skills: false,
    audience: false,
    trainer: false
  });
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (id) {
      loadTraining();
    }
  }, [id]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadTraining = async () => {
    try {
      setIsLoading(true);
      const data = await trainingsService.getTraining(id!);
      setTraining(data);
    } catch (error) {
      setError(t('training.view.loadingError'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatPrice = (price?: number) => {
    if (!price) return t('training.view.free');
    return `${price.toLocaleString()} DZD`;
  };

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm(t('training.deleteSession.confirm'))) {
      return;
    }

    try {
      setDeletingSessionId(sessionId);
      setDeleteError(null);
      await sessionsService.deleteSession(sessionId);
      // Reload training to refresh sessions list
      await loadTraining();
      // Show success message (optional)
      // You could add a toast notification here if you have one
    } catch (error: any) {
      let errorMessage = t('training.deleteSession.error');

      // Check if error message contains subscription info
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setDeleteError(errorMessage);
      alert(errorMessage);
    } finally {
      setDeletingSessionId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{t('training.view.loadingDetails')}</p>
        </motion.div>
      </div>
    );
  }

  if (error || !training) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('training.view.notFound')}</h2>
          <p className="text-gray-600 mb-6">{error || t('training.view.notFoundDescription')}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/training-center/trainings')}
            className="bg-brand-primary text-white px-6 py-3 rounded-lg hover:bg-brand-primary/90 transition-colors font-medium"
          >
            {t('training.view.backToTrainings')}
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <button
              onClick={() => navigate('/training-center/trainings')}
              className="hover:text-brand-primary transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('training.view.myTrainings')}
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">{training.title}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Side - Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Section */}
            <motion.div
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                staggerChildren: 0.1
              }}
            >
              {/* Banner Image */}
              <div className="h-80 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 relative overflow-hidden">
                {training.banner_image ? (
                  <motion.img
                    src={`${API_ENDPOINTS.UPLOADS}/${training.banner_image}`}
                    alt={training.title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                    }}
                  />
                ) : (
                  <motion.div
                    className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="text-center">
                      <BookOpen className="h-20 w-20 text-brand-primary mx-auto mb-4" />
                      <div className="text-lg font-semibold text-brand-primary">{t('training.pharmaceuticalTraining')}</div>
                    </div>
                  </motion.div>
                )}

                {/* Status Badge */}
                <motion.div
                  className="absolute top-6 left-6"
                  initial={{ opacity: 0, x: -30, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <span className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 shadow-lg ${training.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                    training.status === 'refused' ? 'bg-red-100 text-red-700 border-red-200' :
                      'bg-yellow-100 text-yellow-700 border-yellow-200'
                    }`}>
                    {training.status === 'approved' ? t('training.view.approved') :
                      training.status === 'refused' ? t('training.view.rejected') : t('training.view.pending')}
                  </span>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  className="absolute top-6 right-6"
                  initial={{ opacity: 0, x: 30, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate(`/training-center/trainings/${training.id}/edit`)}
                      className="bg-white/95 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-xl shadow-lg border border-white/20 hover:bg-white transition-all"
                    >
                      <Edit className="h-4 w-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/training-center/trainings')}
                      className="bg-white/95 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-xl shadow-lg border border-white/20 hover:bg-white transition-all"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </motion.button>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-8">
                <motion.div
                  className="flex items-start justify-between mb-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.15,
                    delayChildren: 0.2
                  }}
                >
                  <div className="flex-1">
                    <motion.h1
                      className="text-4xl font-bold text-gray-900 mb-3 leading-tight"
                      variants={fadeUpVariants}
                    >
                      {training.title}
                    </motion.h1>
                    <motion.div
                      className="flex items-center gap-3 mb-4"
                      variants={slideInVariants}
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xl font-semibold text-gray-800">
                          {training.training_center?.name || t('training.view.defaultCenter')}
                        </p>
                        <p className="text-gray-600">
                          {formatTrainingLocation(training, t)}
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Meta Information */}
                <motion.div
                  className="mb-8 flex flex-wrap gap-3"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.1,
                    delayChildren: 0.3
                  }}
                >
                  {training.format && (
                    <motion.span
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-secondary to-pink-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <BookOpen className="h-4 w-4" />
                      {training.format}
                    </motion.span>
                  )}
                  {training.language && (
                    <motion.span
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-mint to-teal-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Globe className="h-4 w-4" />
                      {training.language}
                    </motion.span>
                  )}
                  {training.reference && (
                    <motion.span
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-accent to-yellow-600 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FileText className="h-4 w-4" />
                      Ref: {training.reference}
                    </motion.span>
                  )}
                  {training.level && (
                    <motion.span
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-semibold rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Target className="h-4 w-4" />
                      Niveau: {training.level}
                    </motion.span>
                  )}
                </motion.div>

                {/* Sessions with Compact Design */}
                <div className="mb-6">
                  {training.sessions && training.sessions.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-brand-primary" />
                        {t('training.availableSessions')} ({training.sessions.length})
                      </h3>
                      {training.sessions
                        .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime())
                        .map((session, index) => (
                          <div
                            key={session.id}
                            className="group relative overflow-hidden bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 hover:border-brand-primary/30"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <div className="relative">
                                  <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                                    <span className="text-sm font-bold text-white">
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-brand-accent to-yellow-500 rounded-full flex items-center justify-center">
                                    <Clock className="h-2.5 w-2.5 text-white" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 group-hover:text-brand-primary transition-colors">
                                    {t('training.session')} {index + 1}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {formatDate(session.session_date)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <div className="text-xs text-gray-500 mb-1 font-medium">{t('training.timeRemaining')}</div>
                                  <CountdownTimer
                                    targetDate={session.session_date}
                                    variant="card"
                                    className="w-full"
                                  />
                                </div>
                                <button
                                  onClick={() => handleDeleteSession(session.id)}
                                  disabled={deletingSessionId === session.id}
                                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t('training.deleteSession.title')}
                                >
                                  {deletingSessionId === session.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/5 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="bg-gradient-to-r from-brand-primary/10 to-purple-50 rounded-xl p-6 border border-brand-primary/20">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-r from-brand-primary to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Calendar className="h-8 w-8 text-white" />
                          </div>
                          {training.schedule_date ? (
                            <>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('training.nextSession')}</h3>
                              <p className="text-sm text-gray-600 mb-4">
                                {formatDate(training.schedule_date)}
                              </p>
                              <CountdownTimer
                                targetDate={training.schedule_date}
                                variant="card"
                                className="w-full"
                              />
                            </>
                          ) : (
                            <h3 className="text-lg font-semibold text-gray-900">
                              Date de la première session pas encore spécifiée
                            </h3>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Info Grid - Compact Design */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="text-center p-4 bg-gradient-to-br from-brand-primary/5 to-purple-50 rounded-xl border border-brand-primary/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">{training.duration || 'N/A'}</div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.duration')}</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-secondary/5 to-pink-50 rounded-xl border border-brand-secondary/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">{formatTrainingLocation(training, t)}</div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.location', 'Localisation')}</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-mint/5 to-teal-50 rounded-xl border border-brand-mint/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">
                      {training.sessions && training.sessions.length > 0
                        ? new Date(training.sessions[0].session_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                        : training.schedule_date
                          ? new Date(training.schedule_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                          : 'À venir'
                      }
                    </div>
                    <div className="text-xs text-gray-600 font-medium">
                      {training.sessions && training.sessions.length > 1
                        ? t('training.upcomingSessions', { count: training.sessions.length })
                        : t('training.date')
                      }
                    </div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-brand-accent/5 to-yellow-50 rounded-xl border border-brand-accent/10 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-base font-bold text-gray-900">{formatPrice(training.pricing_dzd)}</div>
                    <div className="text-xs text-gray-600 font-medium">{t('training.view.price')}</div>
                  </div>
                </div>

                {/* Modalités & Accès (compact) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="p-6 bg-gradient-to-br from-brand-primary/5 to-purple-50 border border-brand-primary/10 rounded-2xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      {t('training.terms')}
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2">
                      {training.format && <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.view.formatLabel', 'Format')}: {training.format}</li>}
                      {training.language && <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.view.languageLabel', 'Langue')}: {training.language}</li>}
                      {training.level && <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.view.levelLabel', 'Niveau')}: {training.level}</li>}
                      <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-primary rounded-full"></span>{t('training.view.certificateIncluded', 'Certificat inclus')}</li>
                    </ul>
                  </div>
                  <div className="p-6 bg-gradient-to-br from-brand-secondary/5 to-pink-50 border border-brand-secondary/10 rounded-2xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      {t('training.accessDeadlines')}
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-secondary rounded-full"></span>{t('training.view.confirmationDelay', 'Confirmation dans les 48h après inscription')}</li>
                      {training.sessions && training.sessions.length > 0 ? (
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-brand-secondary rounded-full"></span>
                          {training.sessions.length === 1
                            ? `${t('training.view.sessionDate', 'Date de session')}: ${formatDate(training.sessions[0].session_date)}`
                            : `${training.sessions.length} ${t('training.view.sessionsAvailable')}`
                          }
                        </li>
                      ) : (
                        <li className="flex items-center gap-2"><span className="w-2 h-2 bg-brand-secondary rounded-full"></span>{t('training.view.sessionDate', 'Date de session')}: {training.schedule_date ? formatDate(training.schedule_date) : t('training.view.notSpecified', 'Pas encore spécifiée')}</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Download PDF Button */}
                {training.detailed_program_pdf && (
                  <div className="mb-8">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${API_ENDPOINTS.UPLOADS}/${training.detailed_program_pdf}`;
                        link.download = `${training.title}_programme.pdf`;
                        link.target = '_blank';
                        link.click();
                      }}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-lg"
                    >
                      <Download className="h-4 w-4" />
                      {t('training.downloadProgram')}
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Description Section - Collapsible */}
            <ScrollReveal delay={0.1}>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                <button
                  onClick={() => toggleSection('description')}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                >
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-brand-primary" />
                    {t('training.description')}
                  </h2>
                  {expandedSections.description ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections.description && (
                  <div className="px-4 pb-4">
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 leading-relaxed">
                        {training.full_description || t('training.view.noDescription', 'Aucune description disponible.')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </ScrollReveal>

            {/* Learning Objectives Section - Collapsible */}
            {training.learning_objectives && (
              <ScrollReveal delay={0.2}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                  <button
                    onClick={() => toggleSection('objectives')}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                  >
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Target className="h-5 w-5 text-brand-primary" />
                      {t('training.objectives.title')}
                    </h2>
                    {expandedSections.objectives ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.objectives && (
                    <div className="px-4 pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-gray-700 leading-relaxed">
                          {training.learning_objectives}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Skills Gained Section - Collapsible */}
            {training.skills_gained && (
              <ScrollReveal delay={0.3}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                  <button
                    onClick={() => toggleSection('skills')}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                  >
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-brand-primary" />
                      {t('training.skills.title')}
                    </h2>
                    {expandedSections.skills ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.skills && (
                    <div className="px-4 pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-gray-700 leading-relaxed">
                          {training.skills_gained}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Target Audience Section - Collapsible */}
            {training.target_audience && (
              <ScrollReveal delay={0.4}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                  <button
                    onClick={() => toggleSection('audience')}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                  >
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Users className="h-5 w-5 text-brand-primary" />
                      {t('training.audience.title')}
                    </h2>
                    {expandedSections.audience ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.audience && (
                    <div className="px-4 pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-gray-700 leading-relaxed">
                          {training.target_audience}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Trainer Profile Section - Collapsible */}
            {training.trainer_profile_snippets && (
              <ScrollReveal delay={0.5}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                  <button
                    onClick={() => toggleSection('trainer')}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-xl"
                  >
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <User className="h-5 w-5 text-brand-primary" />
                      {t('training.trainerProfile')}
                    </h2>
                    {expandedSections.trainer ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.trainer && (
                    <div className="px-4 pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-gray-700 leading-relaxed">
                          {training.trainer_profile_snippets}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}
          </div>

          {/* Right Side - Statistics */}
          <div className="lg:col-span-1 space-y-6">
            {/* Training Statistics */}
            <ScrollReveal delay={0.3}>
              <motion.div
                variants={slideInVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 px-6 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-brand-primary" />
                    {t('training.view.statistics')}
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">{t('training.views')}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">{training.views || training.viewCount || 0}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">{t('training.view.rating')}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        {training.rating ? `${training.rating}/5` : 'N/A'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-700">{t('training.reviews')}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">{training.reviews || 0}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-medium text-gray-700">{t('training.view.status')}</span>
                      </div>
                      <span className={`text-sm font-semibold px-2 py-1 rounded-full ${training.status === 'approved' ? 'bg-green-100 text-green-800' :
                        training.status === 'refused' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {training.status === 'approved' ? t('training.view.approved') :
                          training.status === 'refused' ? t('training.view.rejected') : t('training.view.pending')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-cyan-600" />
                        <span className="text-sm font-medium text-gray-700">{t('training.view.active')}</span>
                      </div>
                      <span className={`text-sm font-semibold px-2 py-1 rounded-full ${training.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {training.is_active ? t('common.yes') : t('common.no')}
                      </span>
                    </div>

                    {training.sessions && (
                      <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-pink-600" />
                          <span className="text-sm font-medium text-gray-700">{t('training.view.sessions')}</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{training.sessions.length}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>

            {/* Rating Statistics */}
            {training.ratingStats && (
              <ScrollReveal delay={0.4}>
                <motion.div
                  variants={slideInVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Star className="h-5 w-5 text-brand-primary" />
                      {t('training.view.evaluations')}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {typeof training.ratingStats.averageRating === 'number' ? training.ratingStats.averageRating.toFixed(1) : '0.0'}
                        </div>
                        <div className="flex items-center justify-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${star <= (training.ratingStats?.averageRating || 0)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                                }`}
                            />
                          ))}
                        </div>
                        <p className="text-sm text-gray-600">{training.ratingStats.totalReviews} {t('training.reviews')}</p>
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">{t('training.view.distribution')}</h4>
                        {Object.entries(training.ratingStats.ratingDistribution).reverse().map(([rating, count]) => (
                          <div key={rating} className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 w-6">{rating}</span>
                            <Star className="h-3 w-3 text-yellow-400 fill-current" />
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-gradient-to-r from-brand-primary to-brand-secondary h-1.5 rounded-full"
                                style={{
                                  width: `${(training.ratingStats?.totalReviews || 0) > 0 ? (count / (training.ratingStats?.totalReviews || 1)) * 100 : 0}%`
                                }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-900 w-6 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            )}

            {/* Additional Schedule Dates */}
            {training.schedule_dates && (
              <ScrollReveal delay={0.5}>
                <motion.div
                  variants={slideInVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-brand-primary" />
                      {t('training.view.additionalDates')}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{training.schedule_dates}</p>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTrainingPage; 